import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { acceptLegalDocuments } from '../store/legalSlice';
import LegalDocumentSheet from './LegalDocumentSheet';
import '../styles/legal.css';
import { useTranslation } from 'react-i18next';
import { msg, tr } from '../../../i18n/msg';

/** Checkbox wording per document code; the document title after it is the link that opens the text. */
const CONSENT_PREFIX = {
  PRIVACY: msg('legal.consent.privacy', 'I consent to the processing of my personal data under the'),
  OFFER: msg('legal.consent.offer', 'I am 18 or older and accept the'),
  AFFILIATE: msg('legal.consent.affiliate', 'I accept the'),
};
const CONSENT_DEFAULT = msg('legal.consent.default', 'I accept the');

/**
 * Consent window (v1.48.0): shown right after login on the first entry and whenever a document gets a new
 * active version. Cannot be dismissed — the game starts only after every listed document is accepted;
 * acceptance is recorded on the server (user_legal_acceptances).
 */
const LegalConsentModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { documents, accepting, error } = useSelector((state) => state.legal);
  const pending = useMemo(() => documents.filter((d) => !d.accepted), [documents]);
  const isUpdate = pending.length < documents.length;

  const [checked, setChecked] = useState({});
  const [reading, setReading] = useState(null);

  const allChecked = pending.length > 0 && pending.every((d) => checked[d.id]);

  const toggle = (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleAll = () => {
    const next = !allChecked;
    setChecked(Object.fromEntries(pending.map((d) => [d.id, next])));
  };

  const handleAccept = () => {
    if (!allChecked || accepting) return;
    window?.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('medium');
    dispatch(acceptLegalDocuments(pending.map((d) => d.id)));
  };

  if (!pending.length) return null;

  return (
    <div className="legal-consent-overlay">
      <div className="legal-consent" role="dialog" aria-modal="true" aria-labelledby="legal-consent-title">
        <img src="/logo.png" alt="" className="legal-consent__logo" />
        <h2 id="legal-consent-title" className="legal-consent__title">
          {isUpdate ? t('legal.updated_title', 'We updated our terms') : t('legal.welcome_title', 'Welcome to Datum Empire')}
        </h2>
        <p className="legal-consent__subtitle">
          {isUpdate
            ? t('legal.updated_text', 'Please review and accept the updated documents to continue.')
            : t('legal.welcome_text', 'Before you start, please review and accept the documents below.')}
        </p>

        <div className="legal-consent__list">
          {pending.map((d) => (
              <label key={d.id} className="legal-consent__row">
                <input
                  type="checkbox"
                  className="legal-consent__checkbox"
                  checked={!!checked[d.id]}
                  onChange={() => toggle(d.id)}
                />
                <span className="legal-consent__box" aria-hidden="true" />
                <span className="legal-consent__text">
                  {tr(t, CONSENT_PREFIX[d.code] || CONSENT_DEFAULT)}{' '}
                  <button
                    type="button"
                    className="legal-consent__link"
                    onClick={(e) => { e.preventDefault(); setReading(d); }}
                  >
                    {d.title}
                  </button>
                </span>
              </label>
          ))}

          {pending.length > 1 && (
            <label className="legal-consent__row legal-consent__row--all">
              <input type="checkbox" className="legal-consent__checkbox" checked={allChecked} onChange={toggleAll} />
              <span className="legal-consent__box" aria-hidden="true" />
              <span className="legal-consent__text">{t('legal.accept_all', 'Accept all')}</span>
            </label>
          )}
        </div>

        {error && <p className="legal-consent__error">{error}</p>}

        <button
          type="button"
          className="app-btn app-btn--green legal-consent__accept"
          disabled={!allChecked || accepting}
          onClick={handleAccept}
        >
          {accepting ? t('common.saving', 'Saving…') : t('legal.accept_continue', 'Accept & continue')}
        </button>
      </div>

      {reading && (
        <LegalDocumentSheet documentId={reading.id} title={reading.title} onClose={() => setReading(null)} />
      )}
    </div>
  );
};

export default LegalConsentModal;
